import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip, IconButton, Collapse } from '@mui/material';
import { Iconify } from 'src/components';
import { getStorage, setStorage } from 'src/hooks/use-local-storage';

// Per-title persistence key. Each InsightsPanel instance (Cross-Practice
// Insights, Key Insights, etc.) remembers its collapse state independently.
const storageKeyFor = (title) =>
    `insights-panel:collapsed:${(title || 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export default function InsightsPanel({ insights, title }) {
    const storageKey = useMemo(() => storageKeyFor(title), [title]);
    const [collapsed, setCollapsed] = useState(() => Boolean(getStorage(storageKey)?.collapsed));

    // Re-hydrate if the title (and therefore the key) changes after mount.
    useEffect(() => {
        setCollapsed(Boolean(getStorage(storageKey)?.collapsed));
    }, [storageKey]);

    const handleToggle = () => {
        setCollapsed((prev) => {
            const next = !prev;
            setStorage(storageKey, { collapsed: next });
            return next;
        });
    };

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                p: '24px',
                color: '#fff',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: collapsed ? 0 : 2,
                }}
            >
                <Iconify icon="mdi:lightbulb-on" width={20} height={20} />
                <Tooltip title="Auto-generated recommendations based on your practice data." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                    </Box>
                </Tooltip>

                <Tooltip
                    title={collapsed ? 'Expand' : 'Collapse'}
                    placement="top"
                    arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px' } } }}
                >
                    <IconButton
                        size="small"
                        onClick={handleToggle}
                        aria-expanded={!collapsed}
                        aria-label={collapsed ? 'Expand insights' : 'Collapse insights'}
                        sx={{
                            ml: 'auto',
                            color: 'rgba(255,255,255,0.85)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                        }}
                    >
                        <Iconify
                            icon={collapsed ? 'eva:chevron-down-fill' : 'eva:chevron-up-fill'}
                            width={18}
                            height={18}
                        />
                    </IconButton>
                </Tooltip>
            </Box>

            <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {insights.map((insight, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                                p: 1.5,
                                bgcolor: 'rgba(255,255,255,0.1)',
                                borderRadius: '10px',
                            }}
                        >
                            <Typography sx={{ fontSize: '20px', flexShrink: 0 }}>
                                {insight.icon || '💡'}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: 1.5,
                                    '& strong': {
                                        color: '#fbbf24',
                                        fontWeight: 600,
                                    },
                                }}
                                dangerouslySetInnerHTML={{ __html: insight.text || insight }}
                            />
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

InsightsPanel.propTypes = {
    insights: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                icon: PropTypes.string,
                text: PropTypes.string,
            }),
        ])
    ).isRequired,
    title: PropTypes.string,
};

InsightsPanel.defaultProps = {
    title: 'Key Insights',
};
